import { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, ProviderAccreditationPendenciesService, ProviderAccreditationStatusService, Response, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-provider-components';
import { ActionAPI, FetchAPI, FieldAPI, FieldListAPI, FieldListValue, FormAPI, InputAPI, LegendsAPI, LinkAPI, LocatorAPI, PanelAPI, RadioGroupAPI, RadioGroupItem, ToastNotificationAPI, UnboxedAutocompleteAPI, WizardAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnClick, OnClose, OnDynamicContentRender, OnMount, OnSubmit, OnValidate } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, ClickEvent, FetchEvent, MountEvent, RenderEvent, SubmitEvent, ValidateEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject, isEmptyOrBlankString } from '@philips-emr/tws-utils';

import { AccreditationPackagesAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationStatus, AccreditationSteps } from './accreditation-update-controller';
import { ActionTypes, getStampUpdateStatus } from './update-accreditation-type';

interface Package {
  vlPackage: number,
  mainProcedure: LocatorProcedure,
  packageType: RadioGroupItem,
  dsMainProcedure: string,
  nrSeqPackage: number,
  ieComposition: string,
  editable: string,
  nrSeqPackageComposition: string,
  isDetails: boolean,
  preLoadedPackage: string,
}

interface Item {
  ieItemType: string,
  amount: string,
  dsItem: string,
  locatorProcedure: LocatorProcedure,
  medicineMaterialLocator: LocatorMatMed,
  nrSeqItem: number,
  nrSeqPackage: number,
  dsMainProcedure: string,
  ieComposition: string,
  ieAction: ActionTypes,
  dsAction: string,
}

interface LocatorProcedure {
  cdProcedure: number,
  dsProcedure: string,
  ieSourceProcedure: number,
}

interface LocatorMatMed {
  cdMaterial: number,
  dsMaterial: string,
  nrSeqMaterialLocator: number,
}

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
  DETAIL: 'detail',
};

enum EDITABLE {
  NO = 'N',
  YES = 'S',
}

const PROVIDER = 'P';
const HEALTH_INSURANCE = 'O';

const buttomWritePackage = {
  key: PROVIDER,
  label: MessageService.translate('provider.write-package'),
} as RadioGroupItem;
const buttomRequestPackage = {
  key: HEALTH_INSURANCE,
  label: MessageService.translate('provider.request-composition'),
} as RadioGroupItem;

@Controller({ fragment: FragmentNames.ACCREDITATION_PACKAGES })
class AccreditationPackagesController extends BaseController {

  @Inject(Aliases.PANEL_MODAL)
  private panelModal: PanelAPI;
  @Inject(Aliases.FIELD_PROCEDURE)
  private fieldProcedure: FieldAPI;
  @Inject(Aliases.FIELD_MEDICINE_MATERIAL)
  private fieldMedicineMaterial: FieldAPI;
  @Inject(Aliases.LOCATOR_PROCEDURE)
  private locatorProcedure: LocatorAPI;
  @Inject(Aliases.LOCATOR_PROCEDURE_EDIT)
  private locatorProcedureEdit: LocatorAPI;
  @Inject(Aliases.FORM_ITEM)
  private formItem: FormAPI;
  @Inject(Aliases.FORM_ITEM_EDIT)
  private formItemEdit: FormAPI;
  @Inject(Aliases.FIELD_ITEM_LIST)
  private fieldItemList: FieldListAPI;
  @Inject(Aliases.FIELD_ITEM_LIST_EDIT)
  private fieldiItemListEdit: FieldListAPI;
  @Inject(Aliases.WIZARD_PACKAGE)
  private wizardPackage: WizardAPI;
  @Inject(Aliases.FORM_MAIN_PROCEDURE)
  private formMainProcedure: FormAPI;
  @Inject(Aliases.FETCH_PACKAGES)
  private fetchPackages: FetchAPI;
  @Inject(Aliases.PANEL_EDIT)
  private panelEdit: PanelAPI;
  @Inject(Aliases.PANEL_ITEM_LIST_EDIT)
  private panelItemListEdit: PanelAPI;
  @Inject(Aliases.FIELD_PROCEDURE_EDIT)
  private fieldProcedureEdit: FieldAPI;
  @Inject(Aliases.FIELD_MEDICINE_MATERIAL_EDIT)
  private fieldMedicineMaterialEdit: FieldAPI;
  @Inject(Aliases.FIELD_RADIO_BUTTON_EDIT)
  private fieldRadioButtonEdit: FieldAPI;
  @Inject(Aliases.ACTION_ADD)
  private actionAdd: ActionAPI;
  @Inject(Aliases.RADIO_GROUP_PACKAGE_TYPE_EDIT)
  private radioGroupPackageTypeEdit: RadioGroupAPI;
  @Inject(Aliases.RADIO_GROUP_PACKAGE_TYPE)
  private radioGroupPackageType: RadioGroupAPI;
  @Inject(Aliases.PANEL_TITLE)
  private panelTitle: PanelAPI;
  @Inject(Aliases.FORM_EDIT_PACKAGE)
  private formEditPackage: FormAPI;
  @Inject(Aliases.PANEL_FORM_ITEM_EDIT)
  private panelFormItemEdit: PanelAPI;
  @Inject(Aliases.PANEL_LINK_ADD_ITEMS)
  private panelLinkAddItems: PanelAPI;
  @Inject(Aliases.INPUT_VALUE_PACKAGE)
  private inputValue: InputAPI;
  @Inject(Aliases.FIELD_PACKAGE_VALUE)
  private fieldValue: FieldAPI;
  @Inject(Aliases.AUTOCOMPLETE_TYPE_EDIT)
  private unboxedAutocompleteEdit: UnboxedAutocompleteAPI;
  @Inject(Aliases.AUTOCOMPLETE_TYPE)
  private unboxedAutocompleteType: UnboxedAutocompleteAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_PENDENCIES)
  private toastPendencies: ToastNotificationAPI;
  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;
  @Inject(Aliases.LEGENDS)
  private legends: LegendsAPI;

  @OnMount([Aliases.RADIO_GROUP_PACKAGE_TYPE, Aliases.RADIO_GROUP_PACKAGE_TYPE_EDIT])
  onMountRadio(event: MountEvent): void {
    const api = event.target as RadioGroupAPI;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const { ieComposition, nrSeqPackage, ieAction, isDetails } = this.context.getBulkData();
    api.setData([buttomWritePackage, buttomRequestPackage]);
    api.setValue(buttomWritePackage);

    if (api.getAlias() == Aliases.RADIO_GROUP_PACKAGE_TYPE_EDIT
      && (ieComposition == HEALTH_INSURANCE && ieAction != ActionTypes.INCLUDED || isDetails)) {
      this.panelItemListEdit.setVisible(true);
      this.getPackageItems(nrSeqAccreditation as number, nrSeqPackage as number, nrSeqProvider);
    }
  }

  @OnMount([
    Aliases.FIELD_MEDICINE_MATERIAL,
    Aliases.FIELD_MEDICINE_MATERIAL_EDIT,
  ])
  onMountFields(event: MountEvent): void {
    const api = event.target as FieldAPI;
    api.setVisible(false);
  }

  @OnMount([Aliases.FIELD_ITEM_LIST, Aliases.FIELD_ITEM_LIST_EDIT])
  OnMountList(event: MountEvent): void {
    const api = event.target as FieldListAPI;
    api.setValue([]);
  }

  @OnMount(Aliases.FETCH_PACKAGES)
  onMountFetchPackages(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    this.context.set({ nrSeqAccreditation, nrSeqProvider });
    api.setParameters({ nrSeqProvider, nrSeqAccreditation });
    api.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_EDIT_PACKAGE)
  onAfterFetchEditPackage(event: FetchEvent): void {
    const data = event.data as Package;
    const { isDetails } = this.context.getBulkData() as unknown as Package;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;

    if (isDetails) {
      this.formEditPackage.setEditing(false);
      this.formEditPackage.setDisabled(true);
      this.formItemEdit.setEditing(false);
      this.panelFormItemEdit.setVisible(false);
      this.panelLinkAddItems.setVisible(false);
      this.fieldiItemListEdit.setReadOnly(true);
      this.inputValue.setReadOnly(true);
      this.radioGroupPackageTypeEdit.setReadOnly(true);
    }

    if (data) {
      const { ieComposition, nrSeqPackage, nrSeqPackageComposition, preLoadedPackage } = data;

      this.fieldValue.setVisible(preLoadedPackage == 'N');

      if (nrSeqPackageComposition != null) {
        this.radioGroupPackageTypeEdit.setValue(buttomWritePackage);
        this.panelItemListEdit.setVisible(true);
        this.fieldRadioButtonEdit.setVisible(nrSeqPackageComposition == null || isDetails);
        this.getPackageItems(nrSeqAccreditation as number, nrSeqPackage, nrSeqProvider);
        return;
      }

      if (ieComposition == PROVIDER) {
        this.radioGroupPackageTypeEdit.setValue(buttomWritePackage);
        this.panelItemListEdit.setVisible(true);
        this.getPackageItems(nrSeqAccreditation as number, nrSeqPackage, nrSeqProvider);
        return;
      }

      this.panelTitle.setVisible(false);
      this.radioGroupPackageTypeEdit.setValue(buttomRequestPackage);

    }
  }

  @OnMount(Aliases.ACTION_ADD)
  onMountActionAdd(event: MountEvent): void {
    const api = event.target as ActionAPI;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;

    ProviderAccreditationStatusService.getAccreditationStatus(Number(nrSeqAccreditation), Number(nrSeqProvider))
      .then((response) => {
        const { status } = response.body as unknown as Response;
        api.setEnabled(status == AccreditationStatus.AWAITING_REQUESTER || status == AccreditationStatus.AWAITING_REVISION);
      }).catch((response) => ToastService.open(response));
  }

  @OnAfterFetch(Aliases.FETCH_PACKAGES)
  onAfterFetchPackages(event: FetchEvent): void {
    const data = event.data as Package[];
    if (!isEmptyArrayOrObject(data)) {
      const { editable } = data[0];
      this.actionAdd.setEnabled(editable == EDITABLE.YES);
      this.legends.setShowExpression(MessageService.translate('tws.legends.show'));
      this.legends.setHideExpression(MessageService.translate('tws.legends.hide'));
    } else {
      this.legends.setShowExpression('');
      this.legends.setHideExpression('');
    }
  }

  @HPMSOnCreateOption(Aliases.CARD_PROCEDURE)
  onCreateOption(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { editable } = data;

    const show = {
      [OptionKey.DETAIL]: editable == EDITABLE.NO,
      [OptionKey.EDIT]: editable == EDITABLE.YES,
      [OptionKey.DELETE]: editable == EDITABLE.YES,
    };
    shouldRender(show[key]);
  }

  @HPMSOnOptionClick(Aliases.CARD_PROCEDURE)
  onOptionClick(event: HpmsCustomCardEvent<Item>): void {
    const { data, key } = event;
    const { nrSeqPackage, ieComposition, ieAction } = data;
    const action = {
      [OptionKey.DETAIL]: () => this.onDetails(nrSeqPackage, ieComposition, ieAction),
      [OptionKey.DELETE]: () => this.deletePackage(nrSeqPackage),
      [OptionKey.EDIT]: () => this.onEdit(nrSeqPackage, ieComposition, ieAction),
    };
    if (action[key]) {
      action[key]();
    }
  }

  @OnValidate(Aliases.FORM_MAIN_PROCEDURE)
  onValidatePersonalInformationForm(event: ValidateEvent): void {
    const { valid } = event;
    this.wizardPackage.setStepDone('main-package', valid);
  }

  @OnChange(Aliases.RADIO_GROUP_PACKAGE_TYPE)
  onChangeRadioButton(event: ChangeEvent<{ key: string }>): void {
    if (event.value.key == HEALTH_INSURANCE) {
      this.wizardPackage.setHiddenSteps(['packageComposition']);
      return;
    }
    this.wizardPackage.setHiddenSteps([]);
  }

  @OnChange(Aliases.FIELD_RADIO_BUTTON_EDIT)
  onChangeRadioButtonEdit(event: ChangeEvent<{ key: string }>): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const { nrSeqPackage } = this.context.getBulkData();

    if (event.value.key == PROVIDER) {
      this.panelItemListEdit.setVisible(true);
      this.panelTitle.setVisible(true);
      this.getPackageItems(nrSeqAccreditation as number, nrSeqPackage as number, nrSeqProvider);
      return;
    }
    this.panelItemListEdit.setVisible(false);
    this.panelTitle.setVisible(false);
  }

  @OnChange(Aliases.FIELD_ITEM_LIST)
  public onChangeItemList(event: ChangeEvent<FieldListValue>): void {
    this.fieldItemList.setValue(event.value);
    const value = [...this.fieldItemList.getValue()];
    if (value.length > 0) {
      this.wizardPackage.setStepDone('packageComposition', true);
      return;
    }
    this.wizardPackage.setStepDone('packageComposition', false);

  }

  @OnChange(Aliases.FIELD_ITEM_LIST_EDIT)
  public onChangeItemListEdit(event: ChangeEvent<FieldListValue>): void {
    this.fieldiItemListEdit.setValue(event.value);
  }

  @OnMount(Aliases.AUTOCOMPLETE_TYPE)
  onMountAutocomplete(event: MountEvent): void {
    const api = event.target as UnboxedAutocompleteAPI;
    api.setValue(PROVIDER);
  }

  @OnChange(Aliases.FIELD_AUTOCOMPLETE)
  onChangeAutocomplete(event: ChangeEvent<{ ieItemType: string }>): void {
    const ieItemType = event.value as unknown as string;

    if (ieItemType == PROVIDER) {
      this.fieldProcedure.setVisible(true);
      this.fieldMedicineMaterial.setVisible(false);
      this.locatorProcedure.setEnabled(true);
      return;
    }

    this.fieldProcedure.setVisible(false);
    this.fieldMedicineMaterial.setVisible(true);
  }

  @OnChange(Aliases.FIELD_AUTOCOMPLETE_EDIT)
  onChangeAutocompleteEdit(event: ChangeEvent<{ ieItemType: string }>): void {
    const ieItemType = event.value as unknown as string;

    if (ieItemType == PROVIDER) {
      this.fieldProcedureEdit.setVisible(true);
      this.fieldMedicineMaterialEdit.setVisible(false);
      this.locatorProcedureEdit.setEnabled(true);
      return;
    }
    this.fieldProcedureEdit.setVisible(false);
    this.fieldMedicineMaterialEdit.setVisible(true);
  }

  @OnMount(Aliases.AUTOCOMPLETE_TYPE_EDIT)
  onMountAutocompleteEdit(event: MountEvent): void {
    const api = event.target as UnboxedAutocompleteAPI;
    api.setValue(PROVIDER);
  }

  @OnClose(Aliases.MODAL_ADD_PACKAGE)
  onCloseModal(): void {
    this.panelModal.setVisible(false);
  }

  @OnCancel(Aliases.FORM_EDIT_PACKAGE)
  onCancelEdit(): void {
    this.panelEdit.setVisible(false);
  }

  @OnClick([Aliases.LINK_ADD, Aliases.LINK_ADD_ITEM])
  onSaveActionClick(event: ClickEvent): void {
    const api = event.target as LinkAPI;
    const isEdit = api.getAlias() == Aliases.LINK_ADD_ITEM;
    const record = isEdit ? this.formItemEdit.getRecord() as unknown as Item : this.formItem.getRecord() as unknown as Item;
    const valid = isEdit ? this.formItemEdit.isValid() : this.formItem.isValid();

    if (!isEmptyArrayOrObject(record) && valid) {
      const value = isEdit ? [...this.fieldiItemListEdit.getValue()] : [...this.fieldItemList.getValue()];
      const dsItem = record.ieItemType == PROVIDER ?
        String(record.locatorProcedure.cdProcedure) + ' - ' + record.locatorProcedure.dsProcedure :
        String(record.medicineMaterialLocator.nrSeqMaterialLocator) + ' - ' + record.medicineMaterialLocator.dsMaterial;
      const item = {
        ieItemType: record.ieItemType,
        dsItem: dsItem,
        amount: record.amount,
        cdProcedure: record.locatorProcedure?.cdProcedure,
        nrSeqMaterial: record.medicineMaterialLocator?.nrSeqMaterialLocator,
        ieSourceProcedure: record.locatorProcedure?.ieSourceProcedure,
      };
      value.push(item);
      const reversed = [...value].reverse();
      if (isEdit) {
        this.fieldiItemListEdit.setValue(reversed);
        this.formItemEdit.setRecord({});
      } else {
        this.fieldItemList.setValue(reversed);
        this.formItem.setRecord({});
        this.wizardPackage.setStepDone('packageComposition', value.length > 0);
      }

    }
  }

  @OnClick(Aliases.ACTION_ADD)
  onClickAction(): void {
    this.panelModal.setVisible(true);
  }

  @OnSubmit(Aliases.WIZARD_PACKAGE)
  onSubmitWizard(): void {
    const record = this.formMainProcedure.getRecord() as unknown as Package;
    const { packageType } = record;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    let items;

    if (!packageType) {
      record.packageType = buttomWritePackage;
    }
    if (record.packageType.key != HEALTH_INSURANCE) {
      items = this.fieldItemList.getValue();
    }

    if (record.packageType.key == PROVIDER) {
      const type = this.unboxedAutocompleteType.getValue();
      const formComposition = this.formItem.getRecord() as unknown as Item;
      if (this.radioGroupPackageType.getValue()?.key == PROVIDER && type) {
        const dsItem = type == PROVIDER ? formComposition.locatorProcedure.dsProcedure : formComposition.medicineMaterialLocator.dsMaterial;
        if (dsItem) {
          const message = MessageService.translate('provider.package-item-warning').replace('#@DS_ITEM#@', dsItem);
          ToastService.open({ level: ToastLevel.ERROR, message: message });
          return;
        }
      }
    }

    const body = {
      mainProcedure: record.mainProcedure.cdProcedure,
      ieSourceProcedure: record.mainProcedure.ieSourceProcedure,
      vlPackage: record.vlPackage,
      composition: record.packageType.key,
      items,
    };

    HttpService.post<Record<string, Value>>(Endpoint.POST.PACKAGE, { nrSeqProvider, nrSeqAccreditation }, { body })
      .then(() => {
        this.panelModal.setVisible(false);
        this.fetchPackages.setParameters({ nrSeqAccreditation });
        this.fetchPackages.forceFetch();
      }).catch((error) => {
        ToastService.open(error);
      });
  }

  @OnClose(Aliases.MODAL_EDIT)
  onCloseModalEdit(): void {
    this.panelEdit.setVisible(false);
  }

  @OnMount([Aliases.FETCH_EDIT_PACKAGE, Aliases.FETCH_AUTOCOMPLETE_EDIT])
  onMountFetchEdit(event: MountEvent): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const { nrSeqPackage } = this.context.getBulkData();
    this.context.set({ nrSeqAccreditation });
    const api = event.target as FetchAPI;
    api.setParameters({ nrSeqProvider, nrSeqAccreditation, nrSeqPackage });
    api.forceFetch();
  }

  @OnSubmit(Aliases.FORM_EDIT_PACKAGE)
  onSubmitEditItem(event: SubmitEvent): void {
    const api = event.target as FormAPI;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const record = api.getRecord() as unknown as Package;
    const { packageType, ieComposition, nrSeqPackageComposition } = record;
    let items;

    if (!packageType) {
      record.packageType = buttomWritePackage;
      if (ieComposition && ieComposition == HEALTH_INSURANCE && isEmptyOrBlankString(nrSeqPackageComposition)) {
        record.packageType = buttomRequestPackage;
      }
    }

    if (record.packageType.key != HEALTH_INSURANCE) {
      items = this.fieldiItemListEdit.getValue();
      const formComposition = this.formItemEdit.getRecord() as unknown as Item;
      const type = this.unboxedAutocompleteEdit.getValue();

      if (type && (!isEmptyArrayOrObject(formComposition.locatorProcedure) || !isEmptyArrayOrObject(formComposition.medicineMaterialLocator))) {
        const dsItem = type == PROVIDER ? formComposition.locatorProcedure.dsProcedure : formComposition.medicineMaterialLocator.dsMaterial;
        if (dsItem) {
          const message = MessageService.translate('provider.package-item-warning').replace('#@DS_ITEM#@', dsItem);
          ToastService.open({ message, level: ToastLevel.ERROR });
          return;
        }
      }
    }

    const body = {
      vlPackage: record.vlPackage,
      composition: record.packageType.key,
      nrSeqPackage: record.nrSeqPackage,
      items,
    };

    HttpService.post<Record<string, Value>>(Endpoint.POST.PACKAGE_UPDATE, { nrSeqProvider, nrSeqAccreditation }, { body })
      .then(() => {
        this.fetchPackages.forceFetch();
        this.panelEdit.setVisible(false);
      }).catch((error) => ToastService.open(error));

  }

  @OnDynamicContentRender(Aliases.FILED_ACTION_STATUS)
  onRenderCardSpecialty(event: RenderEvent): void {
    const { data, render } = event;
    const { ieAction, dsAction } = data as Item;
    render((): ReactElement => getStampUpdateStatus(ieAction, dsAction));
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendendies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.PACKAGES);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toastPendencies, event);
    }
  }

  private deletePackage(nrSeqPackage: number): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;

    HttpService.delete(Endpoint.DELETE.PACKAGE, { nrSeqProvider, nrSeqAccreditation, nrSeqPackage })
      .then(() => {
        this.fetchPackages.setParameters({ nrSeqProvider, nrSeqAccreditation });
        this.fetchPackages.forceFetch();
      }).catch((response) => {
        ToastService.open(response);
      });
  }

  private onDetails(nrSeqPackage: number, ieComposition: string, ieAction: ActionTypes): void {
    this.context.set({ nrSeqPackage, ieComposition, ieAction, isDetails: true });
    this.panelEdit.setVisible(true);
  }

  private onEdit(nrSeqPackage: number, ieComposition: string, ieAction: ActionTypes): void {
    this.context.set({ nrSeqPackage, ieComposition, ieAction, isDetails: false });
    this.panelEdit.setVisible(true);
  }

  private getPackageItems(nrSeqAccreditation: number, nrSeqPackage: number, nrSeqProvider: number): void {
    this.panelLoader.setVisible(true);
    HttpService.get<Item[]>(Endpoint.GET.PACKAGE_ITEMS, { nrSeqProvider, nrSeqAccreditation, nrSeqPackage })
      .then(({ body }) => {
        if (body) {
          this.fieldiItemListEdit.setValue(body as unknown as Record<string, Value>[]);
          this.panelLoader.setVisible(false);
        }
      }).catch((response) => {
        ToastService.open(response);
        this.panelLoader.setVisible(true);
      });
  }

}

export {
  AccreditationPackagesController,
};
